import { AUTHORITY_MEANING } from "../../auth/authorities";
import { GROUP_LEVEL, LEVELS, LEVEL_NAME, levelOf, type Level } from "../../auth/levels";
import { useAccess } from "../../auth/useAccess";
import { Card, Notice, Page } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Health } from "./Health";

/**
 * Who may do what — your token's groups, the authorities they grant, and
 * every documented route with whether you may call it.
 *
 * Everything here derives from two things: the `cognito:groups` claim on your
 * ID token, and the route catalog (products/routes.ts), which
 * `routes.docs.test.ts` holds equal to docs/products/{labeeb,mcp}.md. There
 * is no hand-kept list of actions: the routes ARE the actions.
 *
 * WHO ENFORCES IT differs by product, and the page says so plainly. Labeeb's
 * Lambda re-verifies the token and requires each route's authority itself
 * (labeeb.md, "Groups and authorities"). MCP does not yet — enforcing the
 * authority table is step 4 of its migration (mcp.md, "Migration status"),
 * not started — so for MCP's routes the console's own gating is the only
 * check.
 */
export function Permissions({ config }: { config: AppConfig }) {
  const access = useAccess();
  const mine = levelOf(access);
  const held = [...access.authorities];

  return (
    <Page title="Permission map" subtitle="Your groups, what they grant, and every documented route.">
      {/* The ladder. Each rung IS a Cognito group — the four are strictly
          nested, which levels.ts asserts in a test rather than assuming — so
          the level is a name for a group and not a second model. L0 is what
          having no group looks like, which is why it names none. */}
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        {LEVELS.map((l) => (
          <LevelCard key={l} level={l} active={l === mine} />
        ))}
      </div>

      <Card title="Your access" note="From the cognito:groups claim on your ID token">
        <p className="text-sm">
          {access.groups.length ? (
            <>
              Groups:{" "}
              {access.groups.map((g, i) => (
                <span key={g}>
                  {i > 0 && ", "}
                  <span className="ref">{g}</span>
                </span>
              ))}
            </>
          ) : (
            "No group. You can read nothing and write nothing."
          )}
        </p>
        {access.unknownGroups.length > 0 && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            {access.unknownGroups.join(", ")} grant{access.unknownGroups.length === 1 ? "s" : ""}{" "}
            nothing: no authority map knows {access.unknownGroups.length === 1 ? "it" : "them"}.
          </p>
        )}
        {held.length > 0 && (
          <ul aria-label="Your authorities" className="mt-3 space-y-1 text-sm">
            {held.map((a) => (
              <li key={a}>
                <span className="ref">{a}</span>
                <span className="text-slate-500 dark:text-slate-400"> — {AUTHORITY_MEANING[a]}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Notice tone="amber" title="Who checks an authority">
        <p>
          <strong>Labeeb enforces</strong> each route&rsquo;s authority itself: it re-verifies your
          ID token and refuses a missing authority with <span className="ref">403</span>.
        </p>
        <p className="mt-2">
          <strong>MCP does not yet.</strong> Enforcing the authority table is step 4 of MCP&rsquo;s
          migration, not started. Until it ships, a greyed-out MCP control is this console
          withholding an action — the same request made directly would not be stopped.
        </p>
        <p className="mt-2">
          A disagreement between this page and a server&rsquo;s answer means your token predates
          a group change: sign out and back in.
        </p>
      </Notice>

      {/* Each product's routes, what each requires, and whether you hold it —
          beside the live probe of whether the product answers at all. */}
      <Health config={config} embedded />
    </Page>
  );
}

/** One rung. Names the group it is, because that is what the token carries. */
function LevelCard({ level, active }: { level: Level; active: boolean }) {
  const group = GROUP_FOR[level];
  return (
    <div
      className={`rounded-lg px-3 py-2 ring-1 ${
        active
          ? "bg-brand-500/10 ring-brand-500/30 dark:ring-brand-300/30"
          : "ring-slate-950/10 dark:ring-white/10"
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{level}</span>
        <span className="truncate text-sm/5 font-medium text-slate-950 dark:text-white">
          {LEVEL_NAME[level]}
        </span>
      </div>
      <p
        className={`truncate font-mono text-[10px] ${
          group ? "text-slate-400 dark:text-slate-500" : "text-amber-700 dark:text-amber-400"
        }`}
        title={group ?? "No group. A signed-in user with none can read nothing and write nothing."}
      >
        {group ?? "no group"}
      </p>
    </div>
  );
}

/** level -> the group that is it. Inverted once, from the same table. */
const GROUP_FOR: Record<Level, string | null> = (() => {
  const out: Record<Level, string | null> = { L0: null, L1: null, L2: null, L3: null, L4: null };
  for (const [group, level] of Object.entries(GROUP_LEVEL)) out[level] = group;
  return out;
})();
